/**
 * Polygon Style Drawer Component
 * Allows users to edit polygon appearance (fill color, stroke, opacity, etc.)
 */
import { colors } from "@/constants/mapStyles";
import CustomSlider from "@components/CustomSlider";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import type { MapPolygon, PolygonStyle } from "@survey-types/survey.polygon.types";
import { useEffect, useRef, useState } from "react";

interface PolygonStyleDrawerProps {
  isOpen: boolean;
  polygon: MapPolygon | null;
  onClose: () => void;
  onSave: (
    polygonId: string,
    style: PolygonStyle,
    name?: string,
    showLabel?: boolean,
    showBorder?: boolean,
  ) => void;
  onDelete?: (polygonId: string) => void;
}

export const PolygonStyleDrawer = ({
  isOpen,
  polygon,
  onClose,
  onSave,
  onDelete,
}: PolygonStyleDrawerProps) => {
  const [name, setName] = useState("");
  const [showLabel, setShowLabel] = useState(true);
  const [isPolygonDetailsOpen, setIsPolygonDetailsOpen] = useState(true);
  const [isPolygonStylingOpen, setIsPolygonStylingOpen] = useState(true);
  const [fillColor, setFillColor] = useState("#3b82f6");
  const [fillOpacity, setFillOpacity] = useState(30);
  const [strokeColor, setStrokeColor] = useState("#1e40af");
  const [showBorder, setShowBorder] = useState(true);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [strokeOpacity, setStrokeOpacity] = useState(100);
  const isHydratingRef = useRef(false);

  useEffect(() => {
    if (polygon) {
      isHydratingRef.current = true;
      setName(polygon.name || "");
      setShowLabel(polygon.showLabel ?? true);
      setFillColor(polygon.style.fillColor);
      setFillOpacity(Math.round((polygon.style.fillOpacity || 0) * 100));
      setStrokeColor(polygon.style.strokeColor);
      // Use showBorder from backend, fallback to checking stroke values if not present
      setShowBorder(
        polygon.showBorder !== undefined
          ? polygon.showBorder
          : (polygon.style.strokeOpacity || 0) > 0 &&
            (polygon.style.strokeWidth || 0) > 0,
      );
      setStrokeWidth(polygon.style.strokeWidth);
      setStrokeOpacity(Math.round((polygon.style.strokeOpacity || 0) * 100));
    }
  }, [polygon]);

  useEffect(() => {
    if (!polygon || !isOpen) return;

    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    const style: PolygonStyle = {
      fillColor,
      fillOpacity: Math.max(0, Math.min(100, fillOpacity)) / 100,
      strokeColor,
      strokeWidth: showBorder ? strokeWidth : 0,
      strokeOpacity: showBorder
        ? Math.max(0, Math.min(100, strokeOpacity)) / 100
        : 0,
    };

    onSave(polygon.polygonId, style, name, showLabel, showBorder);
  }, [
    fillColor,
    fillOpacity,
    isOpen,
    name,
    onSave,
    polygon,
    showBorder,
    showLabel,
    strokeColor,
    strokeOpacity,
    strokeWidth,
  ]);

  const handleDelete = () => {
    if (polygon && onDelete) {
      onDelete(polygon.polygonId);
      onClose();
    }
  };

  const handleColorInput = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    target: "fill" | "stroke",
  ) => {
    const newColor = event.target.value;
    if (newColor.length !== 7) return;
    if (!/^#[0-9A-F]{6}$/i.test(newColor)) return;

    if (target === "fill") {
      setFillColor(newColor);
    } else {
      setStrokeColor(newColor);
    }
  };

  const colorBox = (
    target: "fill" | "stroke",
    selectedColor: string,
    onPick: (color: string) => void,
  ) => (
    <div className="flex flex-col gap-4">
      {Object.keys(colors).map((header) => (
        <div key={header} className="flex flex-col gap-2 mr-13">
          <p className="text-xs">{header}</p>
          <ul className="flex gap-2 flex-wrap">
            {colors[header].map((color: string) => (
              <li
                key={`${target}-${header}-${color}`}
                onClick={() => onPick(color)}
                className="w-6 h-6 rounded-xl min-w-auto cursor-pointer flex border"
                style={{
                  backgroundColor: color,
                  borderColor:
                    selectedColor.toLowerCase() === color.toLowerCase()
                      ? "#0a5c47"
                      : "transparent",
                }}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <EmeraldSideSheet
      opened={isOpen}
      onClose={onClose}
      title="Edit polygon styles"
      position="right"
      closeOnOutsideClick={false}
      hasCloseButton={true}
      closeOnEscape={true}
      className="mapStyleDrawer polygonStyleDrawer"
      hasDivider={true}
    >
      <div className="polygon-style-drawer-layout flex min-h-0 flex-1 flex-col text-data">
        <div className="polygon-style-drawer-scroll min-h-0 flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            <div className="border-b border-gray-200 pb-4">
              <button
                type="button"
                className="w-full flex items-center justify-between text-left"
                onClick={() => setIsPolygonDetailsOpen((prev) => !prev)}
              >
                <span className="text-base font-semibold text-gray-800">
                  Polygon details
                </span>
                <span className="material-icons text-gray-600 text-lg">
                  {isPolygonDetailsOpen ? "expand_less" : "expand_more"}
                </span>
              </button>

              {isPolygonDetailsOpen && (
                <div className="mt-4 flex flex-col gap-3">
                  <EmeraldInput
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Polygon name"
                    className="w-full"
                    label="Polygon name"
                  />
                  <EmeraldCheckbox
                    label="Show polygon name"
                    checked={showLabel}
                    onChange={(checked) => setShowLabel(checked)}
                  />
                </div>
              )}
            </div>

            <div className="border-b border-gray-200 pb-4">
              <button
                type="button"
                className="w-full flex items-center justify-between text-left"
                onClick={() => setIsPolygonStylingOpen((prev) => !prev)}
              >
                <span className="text-base font-semibold text-gray-800">
                  Polygon styling
                </span>
                <span className="material-icons text-gray-600 text-lg">
                  {isPolygonStylingOpen ? "expand_less" : "expand_more"}
                </span>
              </button>

              {isPolygonStylingOpen && (
                <div className="mt-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-3">
                    <p className="text-base font-semibold text-gray-900">
                      Fill color
                    </p>
                    {colorBox("fill", fillColor, setFillColor)}
                    <div className="flex flex-col gap-2">
                      <p className="text-xs">Custom Color</p>
                      <div>
                        <EmeraldInput
                          type="text"
                          label="Enter a hex color code"
                          value={fillColor}
                          inputSize="medium"
                          isValid={true}
                          className="without-label"
                          onChange={(e) => handleColorInput(e, "fill")}
                          maxLength={7}
                        />
                        <p
                          className="w-6 h-6 rounded-full border border-gray-300 mt-2"
                          style={{ backgroundColor: fillColor }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-base font-semibold text-gray-900">
                      Fill opacity
                    </p>
                    <p className="text-sm text-gray-500">
                      Enter a value between 0 and 100
                    </p>
                    <CustomSlider
                      min={0}
                      max={100}
                      step={1}
                      value={fillOpacity}
                      onChange={(value) => setFillOpacity(value)}
                      showInput={true}
                      isInputOnLeft={true}
                      className="polygon-slider"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-gray-900">
                      Border color
                    </p>
                    <EmeraldCheckbox
                      label="Show border"
                      checked={showBorder}
                      onChange={(checked) => setShowBorder(checked)}
                    />
                  </div>

                  {showBorder && (
                    <>
                      {colorBox("stroke", strokeColor, setStrokeColor)}

                      <div className="flex flex-col gap-2">
                        <p className="text-xs">Custom Color</p>
                        <div>
                          <EmeraldInput
                            type="text"
                            label="Enter a hex color code"
                            value={strokeColor}
                            inputSize="medium"
                            isValid={true}
                            className="without-label"
                            onChange={(e) => handleColorInput(e, "stroke")}
                            maxLength={7}
                          />
                          <p
                            className="w-6 h-6 rounded-full border border-gray-300 mt-2"
                            style={{ backgroundColor: strokeColor }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-gray-500">Border opacity</p>
                        <CustomSlider
                          min={0}
                          max={100}
                          step={1}
                          value={strokeOpacity}
                          onChange={(value) => setStrokeOpacity(value)}
                          showInput={true}
                          isInputOnLeft={true}
                          className="polygon-slider"
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-gray-500">Border width</p>
                        <CustomSlider
                          min={1}
                          max={8}
                          step={1}
                          value={strokeWidth}
                          onChange={(value) => setStrokeWidth(value)}
                          showInput={true}
                          isInputOnLeft={true}
                          className="polygon-slider"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Preview */}
            {/* <div>
          <label className="block text-sm font-semibold mb-2 text-gray-700">
            Preview
          </label>
          <div
            className="h-24 rounded-lg flex items-center justify-center relative"
            style={{
              backgroundColor: `${fillColor}${Math.round(fillOpacity * 2.55).toString(16).padStart(2, '0')}`,
              borderColor: showBorder ? `${strokeColor}${Math.round(strokeOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
              borderWidth: showBorder ? `${strokeWidth}px` : "0px",
              borderStyle: "solid",
            }}
          >
            {showLabel && (
              <span
                className="font-semibold text-sm px-3 py-1 rounded"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  color: "#333",
                }}
              >
                {name || "Polygon Name"}
              </span>
            )}
          </div>
        </div> */}
          </div>
        </div>

        {/* Action Buttons */}
        {onDelete && (
          <div className="polygon-style-drawer-footer shrink-0 border-t border-divider-4 bg-white p-4">
            <EmeraldButton
              onClick={handleDelete}
              variant="Secondary"
              className="w-full justify-center font-semibold"
            >
              Delete polygon
            </EmeraldButton>
          </div>
        )}
      </div>
    </EmeraldSideSheet>
  );
};

export default PolygonStyleDrawer;
